import { useNavigate } from 'react-router';
import { LoadingSpinner } from '~/components';
import { NewRecordPill } from '../newRecords/NewRecordPill';
import type { CategoryColumnDef, CategoryStatus, CategorySummary } from './types';

interface CategoryCardProps {
  summary: CategorySummary;
  fullWidth?: boolean;
  tallTwoRows?: boolean;
  /** [MCV-1812] Shows the "New" pill beside the heading. */
  hasNewRecords?: boolean;
}

const borderColorMap: Record<CategoryStatus, string> = {
  error: 'border-l-error-50',
  warning: 'border-l-warning',
  good: 'border-l-good',
  neutral: 'border-l-primary-50',
};

const statusTextColorMap: Record<CategoryStatus, string> = {
  error: 'text-error-50',
  warning: 'text-warning',
  good: 'text-good',
  neutral: 'text-grey-500',
};

const badgeStyleMap: Record<CategoryStatus, string> = {
  error: 'bg-error-t-900 text-error-50',
  warning: 'bg-warning-50 text-warning-900',
  good: 'bg-success-subtle text-good',
  neutral: 'text-grey-500',
};

const MiniTable = ({
  columnDefs,
  items,
}: {
  columnDefs: CategoryColumnDef[];
  items: CategoryCardProps['summary']['items'];
}) => {
  const columnClass = (columnIndex: number) =>
    columnDefs[columnIndex]?.hideAtNarrowWidth ? '@max-[300px]/dashboard-card:hidden' : '';

  return (
    <table className="w-full table-fixed text-sm">
      <thead>
        <tr className="border-b border-grey-900">
          {columnDefs.map((col, columnIndex) => (
            <th
              className={`min-w-0 truncate pb-2 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider text-grey-500 ${columnClass(columnIndex)}`}
              key={col.label}
              title={col.label}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr
            key={`${item.name}-${idx}`}
            className={idx < items.length - 1 ? 'border-b border-base' : ''}
          >
            {(item.columns ?? [item.name]).map((val, colIdx) => (
              <td
                className={`min-w-0 truncate py-1.5 pr-3 text-grey-200 ${columnClass(colIdx)}`}
                key={colIdx}
                title={val}
              >
                {val}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const CategoryCard = ({
  summary,
  fullWidth = false,
  tallTwoRows = false,
  hasNewRecords = false,
}: CategoryCardProps) => {
  const navigate = useNavigate();
  const {
    name,
    count,
    status,
    statusText,
    items,
    loading,
    error,
    navigateTo,
    columnDefs,
    icon: Icon,
  } = summary;

  const emptyBorderColor = 'border-l-grey-600';
  const borderColor = count === 0 && !loading ? emptyBorderColor : borderColorMap[status];

  return (
    <div
      className={`@container/dashboard-card min-w-0 w-full h-full ${
        fullWidth ? 'col-span-1 md:col-span-2' : ''
      } ${tallTwoRows ? 'md:row-span-2' : ''}`}
    >
      <button
        type="button"
        data-cy={`dashboard-category-${summary.key}`}
        className={`flex min-w-0 max-w-full w-full h-full flex-col justify-start overflow-hidden rounded-[10px] border border-grey-900 border-l-4 bg-white p-[18px_20px] text-left shadow-sm transition-shadow duration-150 hover:shadow-md ${borderColor}`}
        onClick={() => navigate(navigateTo)}
        aria-label={`${name} — ${loading ? 'loading' : `${count} ${count === 1 ? 'record' : 'records'}`}${
          // The label replaces the button content, so the visible New pill is not announced otherwise.
          hasNewRecords && !loading ? ', new records added' : ''
        }`}
      >
        {/* Header — [MCV-1495] deliberately has no topic icon. The blank-card
          state below shows the same icon in the centre, and repeating it beside
          the title read as visual noise. */}
        <div className="flex justify-between items-start mb-3.5">
          <div className="flex items-start gap-2.5">
            <div>
              <div className="flex items-center">
                <h3 className="font-semibold text-base text-grey-100">{name}</h3>
                {hasNewRecords && <NewRecordPill />}
              </div>
              {!loading && (
                <p className={`text-[13px] mt-0.5 ${statusTextColorMap[status]}`}>
                  {count === 0 ? '' : statusText}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[28px] font-bold text-primary-50 leading-none">
              {loading ? '-' : count}
            </div>
          </div>
        </div>

        {/* Mini-list */}
        <div className="w-full min-w-0 border-t border-base pt-3">
          {loading && (
            <div className="flex justify-center py-4">
              <LoadingSpinner size={24} />
            </div>
          )}

          {/* TODO: Wire error state once machines expose error status — currently error is always false */}
          {!loading && error && (
            <div className="text-center py-4">
              <p className="text-[15px] text-error-50">Unable to load</p>
            </div>
          )}

          {!loading && !error && count === 0 && (
            <div className="text-center py-4">
              {Icon && <Icon className="size-8 text-grey-600 mx-auto mb-2" aria-hidden="true" />}
              <p className="text-[15px] text-grey-500">No records yet</p>
              <span className="text-[13px] text-primary-50 font-medium mt-1">Add first</span>
            </div>
          )}

          {!loading && !error && count > 0 && (
            <>
              {columnDefs ? (
                <MiniTable columnDefs={columnDefs} items={items} />
              ) : (
                <div className={fullWidth ? 'grid grid-cols-1 md:grid-cols-2 gap-x-6' : ''}>
                  {items.map((item, idx) => (
                    <div
                      key={`${item.name}-${idx}`}
                      className={`flex justify-between items-center py-1.5 ${
                        idx < items.length - 1 ? 'border-b border-base' : ''
                      }`}
                    >
                      <span className="text-sm text-grey-200 truncate mr-2">{item.name}</span>
                      {item.badgeVariant === 'neutral' ? (
                        <span className="text-sm text-grey-500 whitespace-nowrap">
                          {item.badge}
                        </span>
                      ) : (
                        <span
                          className={`text-sm px-2 py-0.5 rounded-full whitespace-nowrap ${badgeStyleMap[item.badgeVariant]}`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div className="mt-3 text-right">
                <span className="text-[13px] text-primary-50 font-medium">
                  View all{count > items.length ? ` (${count})` : ''} &rarr;
                </span>
              </div>
            </>
          )}
        </div>
      </button>
    </div>
  );
};
